import {Box, IconButton, Typography, Paper, Chip, Menu, MenuItem, ListItemIcon, ListItemText, Divider, Button, CircularProgress, Tooltip} from "@mui/material";
import {
    ArrowForward,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Business as CompaniesIcon,
    CalendarToday as CalendarIcon,
    Person as PersonIcon,
    Security as StatusIcon,
    AdminPanelSettings as CreatorIcon,
    ManageAccountsOutlined as ImpersonateIcon,
    ManageAccounts as ManageAccountsIcon,
    EditOutlined,
    Lock as LockIcon,
    ToggleOn,
    ToggleOff,
    DeleteOutline,
    EditOutlined as EditCompaniesIcon,
} from "@mui/icons-material";
import {useCallback, useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import moment from "moment";
import {
    checkCompanyGroupAdminAvailability,
    getCompanyGroupAdmin,
    updateCompanyGroupAdmin,
    enableCompanyGroupAdmin,
    disableCompanyGroupAdmin,
    deleteCompanyGroupAdmin,
} from "../services/system/CompanyGroupAdminService";
import {StatusChip, ConfirmDialog} from "../componenets/ui";
import AddOwnerDialog from "../componenets/company/AddOwnerDialog";
import ChangePasswordDialog from "../componenets/client/ChangePasswordDialog";
import EditAuthorizedCompaniesDialog from "../componenets/administrators/EditAuthorizedCompaniesDialog";
import {getErrorMessage} from "../enums/ErrorMessages";
import {useMessageContext} from "../contexts/MessageContext";
import {useAuth} from "../contexts/AuthContext";
import {t as coachT} from "../componenets/dashboard/coach/coachTokens";
import {formatPhoneForDisplay} from "../componenets/shared/PhoneInput";
import {ReactComponent as ActiveSvg} from "../assets/images/active.svg";
import {ReactComponent as InactiveSvg} from "../assets/images/inactive.svg";

/* ── Who created the admin → Hebrew label (mirrors the trainer profile) ── */
const createdByTypeText = (type) => {
    switch (type) {
        case "SELF": return "רישום עצמאי";
        case "SYSTEM_ADMIN": return "מנהל מערכת";
        case "ADMIN": return "מנהל חברה";
        case "TRAINER": return "מאמן";
        case "ARBOX": return "ARBOX";
        default: return "אין מידע";
    }
};

const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.trim().slice(0, 2).toUpperCase();
};

/* ── Detail cell (one field inside the profile header grid) ── */
const DetailCell = ({icon: Icon, label, value}) => (
    <Box sx={{display: "flex", alignItems: "center", gap: 1.25, minWidth: 0}}>
        <Box sx={{
            width: 34, height: 34, borderRadius: "10px", flexShrink: 0,
            background: coachT.bg, color: coachT.t3,
            display: "flex", alignItems: "center", justifyContent: "center",
        }}>
            <Icon sx={{fontSize: 18}}/>
        </Box>
        <Box sx={{display: "flex", flexDirection: "column", lineHeight: 1.2, minWidth: 0}}>
            <Typography sx={{fontSize: "10.5px", fontWeight: 600, color: coachT.t3, letterSpacing: ".02em"}}>{label}</Typography>
            <Typography sx={{fontSize: "13px", fontWeight: 700, color: coachT.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{value}</Typography>
        </Box>
    </Box>
);

/* ── One row inside the "personal details" card (mirrors TrainerProfileSection) ── */
const ProfileRow = ({icon, label, value}) => (
    <Box sx={{
        display: "flex", alignItems: "center", gap: 1,
        padding: "8px 0", borderBottom: `1px solid ${coachT.borderLight}`, minHeight: 36,
    }}>
        <Box sx={{color: coachT.t3, display: "flex", alignItems: "center", flexShrink: 0, "& .MuiSvgIcon-root": {fontSize: "16px"}}}>
            {icon}
        </Box>
        <Typography sx={{fontSize: "12.5px", fontWeight: 500, color: coachT.t2, flexShrink: 0}}>{label}</Typography>
        <Box sx={{flex: 1}}/>
        {typeof value === "string" ? (
            <Typography sx={{fontSize: "13px", fontWeight: 700, color: coachT.text, textAlign: "left", wordBreak: "break-word"}}>{value}</Typography>
        ) : (value)}
    </Box>
);

/* ── Card shell shared by the two sections ── */
const SectionCard = ({icon: Icon, title, badge, action, children}) => (
    <Paper
        elevation={0}
        sx={{
            backgroundColor: coachT.white, border: `1px solid ${coachT.border}`,
            borderRadius: coachT.r, boxShadow: coachT.sh, overflow: "hidden", direction: "rtl",
        }}
    >
        <Box sx={{display: "flex", alignItems: "center", gap: 1.5, padding: "16px 22px", backgroundColor: coachT.bg, borderBottom: `1px solid ${coachT.border}`}}>
            <Box sx={{width: 40, height: 40, borderRadius: "11px", background: coachT.white, border: `1px solid ${coachT.border}`, display: "flex", alignItems: "center", justifyContent: "center"}}>
                <Icon sx={{color: coachT.t2, fontSize: 22}}/>
            </Box>
            <Typography sx={{fontWeight: 800, fontSize: "17px", color: coachT.text, letterSpacing: "-0.2px"}}>{title}</Typography>
            {badge != null && (
                <Chip label={badge} size="small" sx={{backgroundColor: coachT.greenLight, color: coachT.greenText, fontWeight: 700, fontSize: "11px", height: "22px", marginRight: "6px"}}/>
            )}
            {action && <Box sx={{flex: 1}}/>}
            {action}
        </Box>
        {children}
    </Paper>
);

const CompanyGroupAdminPage = () => {
    const params = useParams();
    const navigate = useNavigate();
    const {notificationHandler} = useMessageContext();
    // Editing/managing a group admin is a super-admin-only capability: the whole action menu, the
    // companies "edit" affordance and impersonation are gated on it (a group admin can reach this page
    // by URL but never mutate or impersonate).
    const {isSuperAdmin, impersonate} = useAuth();

    const [admin, setAdmin] = useState();
    const [reload, setReload] = useState(false);
    const [impersonating, setImpersonating] = useState(false);

    const [menuAnchor, setMenuAnchor] = useState(null);
    const menuOpen = Boolean(menuAnchor);
    const [editOpen, setEditOpen] = useState(false);
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [editCompaniesOpen, setEditCompaniesOpen] = useState(false);

    useEffect(() => {
        getCompanyGroupAdmin(params.adminId)
            .then(r => setAdmin(r.data))
            .catch(e => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.adminId, reload]);

    const companies = admin?.companies || [];
    const canManage = isSuperAdmin();

    const refresh = () => setReload(prev => !prev);

    // Live "already taken?" probe for the edit dialog's on-blur validation. `excludeUserId` is this
    // admin, so their OWN current email/phone/name is never reported as a collision with themselves.
    const checkAvailability = useCallback(
        ({email, phoneNumber, name}) => checkCompanyGroupAdminAvailability({
            email, phoneNumber, name, excludeUserId: admin?.id,
        }),
        [admin?.id],
    );

    // Edit profile — reuses the company Add/Edit-Owner dialog in edit mode (name/email/phone only).
    const handleUpdateProfile = (payload) => {
        updateCompanyGroupAdmin(admin.id, {name: payload.name, email: payload.email, phoneNumber: payload.phoneNumber})
            .then(() => { setEditOpen(false); notificationHandler.success("הפרטים עודכנו בהצלחה"); refresh(); })
            .catch(e => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)));
    };

    const handleChangePassword = (password) => {
        updateCompanyGroupAdmin(admin.id, {password})
            .then(() => { setChangePasswordOpen(false); notificationHandler.success("סיסמה שונתה בהצלחה"); })
            .catch(e => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)));
    };

    const handleToggleEnabled = () => {
        const action = admin.enabled ? disableCompanyGroupAdmin : enableCompanyGroupAdmin;
        const msg = admin.enabled ? "מנהל קבוצת חברות נחסם בהצלחה" : "מנהל קבוצת חברות הופעל בהצלחה";
        action(admin.id)
            .then(() => { notificationHandler.success(msg); refresh(); })
            .catch(e => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)));
    };

    const handleDelete = () => {
        deleteCompanyGroupAdmin(admin.id)
            .then(() => navigate("/app/administrators"))
            .then(() => notificationHandler.success("מנהל קבוצת חברות נמחק בהצלחה"))
            .catch(e => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)));
    };

    // Step INTO this group admin (like company impersonation, but the target is the company-less
    // group-admin persona itself — SYSTEM_ADMIN only). AuthContext swaps the token + reloads the
    // identity; we then land on the dashboard since this page (/app/administrators) is SA-only and a
    // group admin can't view it.
    const handleImpersonate = async () => {
        if (!admin) return;
        setImpersonating(true);
        try {
            await impersonate(admin.id, admin.name);
            navigate("/app/dashboard");
        } catch (e) {
            notificationHandler.error("ההתחזות נכשלה");
        } finally {
            setImpersonating(false);
        }
    };

    const menuItems = [
        {label: "ערוך פרופיל", icon: <EditOutlined fontSize="small"/>, onClick: () => { setEditOpen(true); setMenuAnchor(null); }},
        {label: "שנה סיסמה", icon: <LockIcon fontSize="small"/>, onClick: () => { setChangePasswordOpen(true); setMenuAnchor(null); }},
        {divider: true},
        {
            label: admin?.enabled ? "חסום מנהל קבוצה" : "הפעל מנהל קבוצה",
            icon: admin?.enabled ? <ToggleOff fontSize="small"/> : <ToggleOn fontSize="small"/>,
            onClick: () => { handleToggleEnabled(); setMenuAnchor(null); },
            color: admin?.enabled ? coachT.red : coachT.green,
        },
        {
            label: "מחק מנהל קבוצה",
            icon: <DeleteOutline fontSize="small"/>,
            onClick: () => { setDeleteOpen(true); setMenuAnchor(null); },
            color: coachT.red,
        },
    ];

    return (
        <Box sx={{display: "flex", flexDirection: "column", gap: "18px"}}>

            {/* ─── Back link ─────────────────────────────────── */}
            <Box sx={{display: "flex", alignItems: "center", gap: 1.25, flexShrink: 0}}>
                <IconButton
                    onClick={() => navigate("/app/administrators")}
                    sx={{color: coachT.t2, p: 1, "&:hover": {bgcolor: coachT.greenLight, color: coachT.green}}}
                >
                    <ArrowForward/>
                </IconButton>
                <Typography sx={{fontSize: "13px", fontWeight: 600, color: coachT.t2}}>חזרה למנהלי קבוצות חברות</Typography>
            </Box>

            {admin && (
                <>
                    {/* ─── Profile header card ───────────────────── */}
                    <Box sx={{background: coachT.white, border: `1px solid ${coachT.border}`, borderRadius: coachT.r, boxShadow: coachT.sh, overflow: "hidden"}}>
                        {/* Identity row */}
                        <Box sx={{
                            display: "flex", alignItems: "center", gap: 1.25,
                            padding: {xs: "14px 16px", md: "16px 22px"},
                            borderBottom: `1px solid ${coachT.border}`,
                            background: `linear-gradient(135deg, ${coachT.greenLight}, #f4fbf8)`,
                        }}>
                            <Box sx={{minWidth: 0, display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap"}}>
                                <Typography sx={{fontSize: {xs: "21px", md: "25px"}, fontWeight: 800, color: coachT.text, lineHeight: 1.2, letterSpacing: "-0.3px"}}>
                                    {admin.name || "פרטי מנהל"}
                                </Typography>
                                {admin.enabled
                                    ? <StatusChip label="פעיל" variant="success" icon={<ActiveSvg style={{width: 11, height: 11}}/>} size="small"/>
                                    : <StatusChip label="חסום" variant="error" icon={<InactiveSvg style={{width: 11, height: 11}}/>} size="small"/>}
                            </Box>
                            <Box sx={{flex: 1}}/>

                            {/* Impersonate — super-admin only. Disabled targets have no usable session
                                (the server rejects them), so gate on `enabled` with an explaining tooltip. */}
                            {canManage && (
                                <Tooltip title={admin.enabled ? "" : "לא ניתן להתחזות למנהל חסום"}>
                                    <span>
                                        <Button
                                            onClick={handleImpersonate}
                                            disabled={impersonating || !admin.enabled}
                                            startIcon={impersonating
                                                ? <CircularProgress size={16} color="inherit"/>
                                                : <ImpersonateIcon/>}
                                            sx={{
                                                textTransform: "none", fontWeight: 700, fontSize: "13px",
                                                color: coachT.green, background: coachT.white,
                                                border: `1px solid ${coachT.green}`, borderRadius: coachT.rs,
                                                height: 40, px: 1.75, flexShrink: 0,
                                                "&:hover": {background: coachT.greenLight, borderColor: coachT.green},
                                                "&.Mui-disabled": {color: coachT.t3, borderColor: coachT.border},
                                            }}
                                        >
                                            התחזה
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}

                            {/* Settings-management menu — super-admin only (reused from the trainer/admin profile) */}
                            {canManage && (
                                <>
                                    <IconButton
                                        onClick={e => setMenuAnchor(e.currentTarget)}
                                        sx={{
                                            color: coachT.t2, border: `1px solid ${coachT.border}`, background: coachT.white,
                                            borderRadius: coachT.rs, width: 40, height: 40, flexShrink: 0,
                                            "&:hover": {bgcolor: coachT.greenLight, borderColor: coachT.green, color: coachT.green},
                                        }}
                                    >
                                        <ManageAccountsIcon/>
                                    </IconButton>
                                    <Menu
                                        anchorEl={menuAnchor} open={menuOpen}
                                        onClose={() => setMenuAnchor(null)}
                                        anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                                        transformOrigin={{vertical: "top", horizontal: "left"}}
                                        PaperProps={{sx: {borderRadius: coachT.rs, border: `1px solid ${coachT.border}`, boxShadow: coachT.sh, mt: 0.5, minWidth: 200}}}
                                    >
                                        {menuItems.map((item, i) =>
                                            item.divider ? (
                                                <Divider key={`d-${i}`} sx={{my: 0.5}}/>
                                            ) : (
                                                <MenuItem
                                                    key={item.label}
                                                    onClick={item.onClick}
                                                    sx={{
                                                        fontSize: "14px", fontWeight: 500, py: 1.25, px: 2,
                                                        mx: 0.75, borderRadius: coachT.rxs,
                                                        color: item.color || coachT.text,
                                                        "&:hover": {bgcolor: item.color ? `${item.color}14` : coachT.greenLight, color: item.color || coachT.green},
                                                    }}
                                                >
                                                    <ListItemIcon sx={{color: "inherit", minWidth: 32}}>{item.icon}</ListItemIcon>
                                                    <ListItemText primaryTypographyProps={{fontSize: "14px", fontWeight: 500}}>{item.label}</ListItemText>
                                                </MenuItem>
                                            )
                                        )}
                                    </Menu>
                                </>
                            )}
                        </Box>

                        {/* Details grid */}
                        <Box sx={{
                            display: "grid",
                            gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)"},
                            gap: {xs: 1.75, md: 2.25},
                            padding: {xs: "16px", md: "18px 22px"},
                        }}>
                            <DetailCell icon={CompaniesIcon} label="חברות מורשות" value={companies.length.toLocaleString()}/>
                            {admin.email && <DetailCell icon={EmailIcon} label="מייל" value={admin.email}/>}
                            {admin.phoneNumber && <DetailCell icon={PhoneIcon} label="טלפון" value={formatPhoneForDisplay(admin.phoneNumber)}/>}
                        </Box>
                    </Box>

                    {/* ─── Personal details ──────────────────────── */}
                    <SectionCard icon={PersonIcon} title="פרטים אישיים">
                        <Box sx={{padding: "8px 22px 12px"}}>
                            <Box sx={{
                                display: "grid",
                                gridTemplateColumns: {xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)"},
                                columnGap: "24px",
                            }}>
                                <ProfileRow icon={<PersonIcon/>} label="שם מלא" value={admin.name || "אין מידע"}/>
                                <ProfileRow
                                    icon={<StatusIcon/>}
                                    label="סטטוס"
                                    value={
                                        <Chip
                                            label={admin.enabled ? "פעיל" : "חסום"}
                                            size="small"
                                            sx={{backgroundColor: admin.enabled ? coachT.greenLight : coachT.redLight, color: admin.enabled ? coachT.greenText : coachT.redText, fontWeight: 600, fontSize: "11px", height: "20px"}}
                                        />
                                    }
                                />
                                <ProfileRow icon={<EmailIcon/>} label="מייל" value={admin.email || "אין מידע"}/>
                                <ProfileRow
                                    icon={<PhoneIcon/>}
                                    label="טלפון"
                                    value={<Typography sx={{direction: "ltr", fontSize: "13px", fontWeight: 700, color: coachT.text}}>{formatPhoneForDisplay(admin.phoneNumber) || "אין מידע"}</Typography>}
                                />
                                <ProfileRow icon={<CalendarIcon/>} label="תאריך יצירה" value={admin.createdDate ? moment(admin.createdDate * 1000).format("DD/MM/YY") : "אין מידע"}/>
                                <ProfileRow icon={<CreatorIcon/>} label="נוצר על ידי" value={createdByTypeText(admin.createdByType)}/>
                            </Box>
                        </Box>
                    </SectionCard>

                    {/* ─── Authorized companies ──────────────────── */}
                    <SectionCard
                        icon={CompaniesIcon}
                        title="חברות מורשות להתחזות"
                        badge={companies.length}
                        action={canManage && (
                            <IconButton
                                aria-label="ערוך חברות מורשות"
                                onClick={() => setEditCompaniesOpen(true)}
                                sx={{
                                    color: coachT.t2, border: `1px solid ${coachT.border}`, background: coachT.white,
                                    borderRadius: coachT.rs, width: 36, height: 36, flexShrink: 0,
                                    "&:hover": {bgcolor: coachT.greenLight, borderColor: coachT.green, color: coachT.green},
                                }}
                            >
                                <EditCompaniesIcon sx={{fontSize: 18}}/>
                            </IconButton>
                        )}
                    >
                        {companies.length === 0 ? (
                            <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, padding: "40px", direction: "rtl"}}>
                                <CompaniesIcon sx={{fontSize: 40, color: coachT.border}}/>
                                <Typography sx={{color: coachT.t3, fontSize: "13px", fontWeight: 500}}>אין חברות מורשות</Typography>
                            </Box>
                        ) : (
                            <Box sx={{direction: "rtl"}}>
                                {companies.map(company => (
                                    <Box
                                        key={company.id}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => navigate(`/app/companies/${company.id}`)}
                                        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/app/companies/${company.id}`); } }}
                                        sx={{
                                            display: "flex", alignItems: "center", gap: 1.5,
                                            padding: "12px 22px", borderBottom: `1px solid ${coachT.borderLight}`,
                                            "&:last-of-type": {borderBottom: "none"},
                                            cursor: "pointer", transition: "background .12s ease",
                                            "&:hover": {background: coachT.greenLight},
                                        }}
                                    >
                                        <Box sx={{
                                            width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                                            background: coachT.greenLight, color: coachT.greenText,
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            fontSize: "12px", fontWeight: 700,
                                        }}>
                                            {getInitials(company.name)}
                                        </Box>
                                        <Typography sx={{fontSize: "13.5px", fontWeight: 600, color: coachT.text, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                                            {company.name}
                                        </Typography>
                                        <Chip
                                            icon={<ImpersonateIcon sx={{fontSize: 14}}/>}
                                            label="מורשה להתחזות"
                                            size="small"
                                            sx={{backgroundColor: coachT.bg, color: coachT.t2, border: `1px solid ${coachT.border}`, fontWeight: 600, fontSize: "11px", height: "24px", "& .MuiChip-icon": {color: coachT.t2}}}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </SectionCard>

                    {/* ─── Super-admin management dialogs ─────────── */}
                    {canManage && (
                        <>
                            <AddOwnerDialog
                                open={editOpen}
                                close={() => setEditOpen(false)}
                                owner={admin}
                                companyId={null}
                                handleSubmit={handleUpdateProfile}
                                title="עריכת מנהל קבוצת חברות"
                                allowExistingUser={false}
                                checkAvailability={checkAvailability}
                                validateNameUniqueness
                            />
                            <ChangePasswordDialog
                                isOpen={changePasswordOpen}
                                close={() => setChangePasswordOpen(false)}
                                onSubmit={handleChangePassword}
                            />
                            <EditAuthorizedCompaniesDialog
                                open={editCompaniesOpen}
                                close={() => setEditCompaniesOpen(false)}
                                admin={admin}
                                onSaved={refresh}
                            />
                            <ConfirmDialog
                                open={deleteOpen}
                                onClose={() => setDeleteOpen(false)}
                                onConfirm={handleDelete}
                                title="מחיקת מנהל קבוצת חברות"
                                description={`האם למחוק את ${admin.name}? פעולה זו אינה ניתנת לביטול.`}
                                confirmText="מחק"
                                variant="danger"
                            />
                        </>
                    )}
                </>
            )}
        </Box>
    );
};

export default CompanyGroupAdminPage;
